import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import React, { useRef, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { uploadImage } from "../../redux/slices/common";
import { useSelector } from "react-redux";
export default function AddBannerModal({ closeModal }) {
  const { modalData } = useSelector((state) => state.modal);
  const { type: imageType, handleSave: onSave, viewData } = modalData;

  // Check if we're in view mode
  const isViewMode = !!viewData;

  // Initialize state
  const [mediaType, setMediaType] = useState(viewData?.mediaType || "");
  const [file, setFile] = useState(viewData?.imageUrl || null);
  const [fileUrl, setFileUrl] = useState(viewData?.imageUrl || "");
  const [redirectUrl, setRedirectUrl] = useState(viewData?.redirectUrl || "");
  const [altText, setAltText] = useState(viewData?.altText || "");
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  // Set initial values when viewData changes
  useEffect(() => {
    if (viewData) {
      setMediaType(viewData.mediaType || "");
      setFile(viewData.imageUrl || null);
      setFileUrl(viewData.imageUrl || "");
      setRedirectUrl(viewData.redirectUrl || "");
      setAltText(viewData.altText || "");
    }
  }, [viewData]);

  const handleMediaTypeChange = (event) => {
    if (isViewMode) return;
    setMediaType(event.target.value);
    setFile(null);
    setFileUrl("");
  };

  const handleTextChange = (event) => {
    if (isViewMode) return;
    setRedirectUrl(event.target.value);
  };

  const handleAltChange = (event) => {
    if (isViewMode) return;
    setAltText(event.target.value);
  };

  const handleFileChange = (event) => {
    if (isViewMode) return;
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type.split("/")[0];

      if (fileType === "image" || fileType === "video") {
        setFile(URL.createObjectURL(selectedFile));
        setIsUploading(true);

        const body = new FormData();
        body.append("filePath", selectedFile);

        dispatch(uploadImage(body))
          .unwrap()
          .then((res) => {
            setFileUrl(res.fileUrl);
            setIsUploading(false);
          })
          .catch((err) => {
            setIsUploading(false);
          });
      } else {
        alert("Please upload an image or a video file.");
      }
    }
  };

  const handleFileBoxClick = () => {
    if (isViewMode) return;
    fileInputRef.current.click();
  };

  const onHandleSave = () => {
    if (imageType === "desktop") {
      const desktopImages = {
        mediaType: mediaType,
        imageUrl: fileUrl,
        redirectUrl: redirectUrl,
        altText: altText,
      };
      onSave({ desktopImages, imageType, redirectUrl, altText });
      closeModal();
    } else if (imageType === "mobile") {
      const mobileImages = {
        mediaType: mediaType,
        imageUrl: fileUrl,
        redirectUrl: redirectUrl,
        altText: altText,
      };
      onSave({ mobileImages, imageType, redirectUrl, altText });
      closeModal();
    }
  };

  return (
    <>
      <DialogTitle
        id="draggable-dialog-title"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        {isViewMode ? "View Banner" : "Add Banner"}
        <IconButton onClick={closeModal} edge="end" size="small">
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={3} pt={1} mb={3}>
          <FormControl fullWidth>
            <InputLabel>Media Type</InputLabel>
            <Select
              className="form-select"
              value={mediaType}
              onChange={handleMediaTypeChange}
              fullWidth
              variant="outlined"
              label="Media Type"
              disabled={isViewMode}
            >
              <MenuItem value="image">Image</MenuItem>
              <MenuItem value="video">Video</MenuItem>
            </Select>
          </FormControl>

          <TextField
            value={redirectUrl}
            onChange={handleTextChange}
            fullWidth
            variant="outlined"
            label="Redirect URL"
            disabled={isViewMode}
          />
        </Stack>

        <Stack direction="column" spacing={3} className="trait-image-upload">
          <Box
            bgcolor="#F1F1F1"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            width="130px"
            height="130px"
            borderRadius="20px"
            p={2}
            sx={{ cursor: isViewMode ? "default" : "pointer" }}
            onClick={handleFileBoxClick}
          >
            {isUploading ? (
              <CircularProgress size={32} color="primary" />
            ) : file ? (
              mediaType === "image" ? (
                <img
                  src={file}
                  alt="Uploaded"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "20px",
                  }}
                />
              ) : (
                <video
                  src={file}
                  controls
                  style={{ width: "100%", height: "100%", borderRadius: "20px" }}
                />
              )
            ) : (
              <Typography variant="h5" fontWeight="medium" textAlign="center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="56"
                  height="57"
                  viewBox="0 0 56 57"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M48.4163 21.0987C48.4163 19.3977 47.7397 17.7667 46.538 16.5627C44.291 14.318 39.8483 9.87533 37.6037 7.62833C36.3997 6.42667 34.7687 5.75 33.0677 5.75C30.0227 5.75 23.921 5.75 18.6663 5.75C15.7263 5.75 12.9077 6.91667 10.8287 8.99567C8.74967 11.0747 7.58301 13.8933 7.58301 16.8333V40.1667C7.58301 46.287 12.546 51.25 18.6663 51.25H37.333C43.4533 51.25 48.4163 46.287 48.4163 40.1667C48.4163 33.4257 48.4163 24.8693 48.4163 21.0987ZM33.2497 9.25467C33.189 9.25233 33.1283 9.25 33.0677 9.25C30.0227 9.25 23.921 9.25 18.6663 9.25C16.655 9.25 14.7253 10.048 13.3043 11.4713C11.881 12.8923 11.083 14.822 11.083 16.8333V40.1667C11.083 44.355 14.478 47.75 18.6663 47.75H37.333C41.5213 47.75 44.9163 44.355 44.9163 40.1667C44.9163 33.4257 44.9163 24.8693 44.9163 21.0987C44.9163 21.038 44.914 20.9773 44.9117 20.9167H39.6663C37.9653 20.9167 36.332 20.24 35.128 19.0383C33.9263 17.8343 33.2497 16.201 33.2497 14.5V9.25467ZM42.4407 17.4167L36.7497 11.7257V14.5C36.7497 15.2747 37.0577 16.0143 37.6037 16.5627C38.152 17.1087 38.8917 17.4167 39.6663 17.4167H42.4407Z"
                    fill="#0163FF"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M28.0001 22.0835H28.0421C28.2031 22.0882 28.3594 22.1138 28.5064 22.1582L28.5111 22.1605C28.7771 22.2398 29.0267 22.3868 29.2367 22.5968L33.9034 27.2635C34.5871 27.9448 34.5871 29.0555 33.9034 29.7368C33.2221 30.4205 32.1114 30.4205 31.4301 29.7368L29.7501 28.0592V35.5002C29.7501 36.4662 28.9661 37.2502 28.0001 37.2502C27.0341 37.2502 26.2501 36.4662 26.2501 35.5002V28.0592L24.5701 29.7368C23.8887 30.4205 22.7781 30.4205 22.0967 29.7368C21.4131 29.0555 21.4131 27.9448 22.0967 27.2635L26.7634 22.5968C26.9734 22.3868 27.2231 22.2398 27.4891 22.1605L27.4937 22.1582C27.6407 22.1138 27.7971 22.0882 27.9581 22.0835H28.0001Z"
                    fill="#0163FF"
                  />
                </svg>
              </Typography>
            )}
          </Box>
          <Typography
            style={{ fontSize: "16px", marginTop: "10px" }}
            fontWeight="bold"
            textAlign="center"
          >
            {`${
              isViewMode
                ? mediaType === "image"
                  ? "Banner Image"
                  : "Banner Video"
                : mediaType !== "video"
                ? "Upload Image"
                : "Upload Video"
            }(${imageType === "desktop" || imageType === "desktopImages" ? "652x323" : "575x300"})`}
          </Typography>

          {!isViewMode && (
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*,video/*"
              onChange={handleFileChange}
            />
          )}

          {mediaType !== "video" && (
            <Box pt={3} style={{ width: "100%" }}>
              <TextField
                value={altText}
                onChange={handleAltChange}
                fullWidth
                variant="outlined"
                label="Alt Tag"
                disabled={isViewMode}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions style={{ justifyContent: "center", marginBottom: "12px" }}>
        <MDButton
          onClick={closeModal}
          sx={{ minWidth: "160px" }}
          type="button"
          variant="outlined"
          color="dark"
        >
          {isViewMode ? "Close" : "Cancel"}
        </MDButton>
        {!isViewMode && (
          <MDButton
            onClick={onHandleSave}
            disabled={isUploading || !mediaType || !fileUrl}
            sx={{ minWidth: "160px" }}
            color="primary"
            type="button"
            variant="contained"
          >
            Save
          </MDButton>
        )}
      </DialogActions>
    </>
  );
}
